/** Запись фразой. Главный путь внесения траты — поле сразу на экране, а не за кнопкой.
 *
 * Отвечает на все ветки того же роутера, что обслуживает чат: уточнение валюты,
 * подтверждение крупной суммы, перевод между своими, непонятая фраза. Ветку
 * «не поддерживается» тоже показываем — молчание после нажатия читается как поломка.
 *
 * Тон без осуждения: ни одного восклицательного знака, ни «неверно», ни «ошибка».
 */
import { useEffect, useRef, useState } from "react";
import { ApiError, api, type RecordOut } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { tap } from "../lib/telegram.ts";
import { useOnce } from "../lib/once.ts";
import { startRecording, voiceSupport, type Recording } from "../lib/voice.ts";

type State =
  | { at: "idle" }
  | { at: "recording"; since: number }
  | { at: "sending" }
  | { at: "answer"; out: RecordOut }
  | { at: "trouble"; text: string };

export function RecordInput({ onRecorded }: { onRecorded: () => void }) {
  const [text, setText] = useState("");
  const [state, setState] = useState<State>({ at: "idle" });

  const handle = (out: RecordOut) => {
    if (out.kind === "recorded") {
      setText("");
      onRecorded();
    }
    setState({ at: "answer", out });
  };

  const once = useOnce();

  const run = (call: () => Promise<RecordOut>) => {
    tap();
    setState({ at: "sending" });
    once(() => call()
      .then(handle)
      .catch((e) =>
        setState({
          at: "trouble",
          text:
            e instanceof ApiError && e.kind === "draft-lost"
              ? "Пока думали, связь оборвалась. Набери фразу заново."
              : e instanceof ApiError && e.kind === "offline"
                ? "Нет связи. Попробуй ещё раз."
                : "Не получилось записать. Попробуй ещё раз.",
        }),
      ));
  };

  const recorder = useRef<Recording | null>(null);
  const [seconds, setSeconds] = useState(0);
  const canRecord = voiceSupport() === "ready";

  // Таймер нужен не для красоты: без него непонятно, идёт ли запись вообще.
  useEffect(() => {
    if (state.at !== "recording") return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [state.at]);

  // Ушли с экрана посреди записи — микрофон обязан освободиться.
  useEffect(() => () => recorder.current?.cancel(), []);

  const startVoice = () => {
    tap();
    setSeconds(0);
    startRecording()
      .then((rec) => {
        recorder.current = rec;
        setState({ at: "recording", since: Date.now() });
      })
      .catch(() =>
        setState({
          at: "trouble",
          text: "Микрофон закрыт. Разреши доступ в настройках Telegram — или набери текстом.",
        }),
      );
  };

  const stopVoice = (send: boolean) => {
    tap();
    const rec = recorder.current;
    recorder.current = null;
    if (!rec) return;
    if (!send) {
      rec.cancel();
      setState({ at: "idle" });
      return;
    }
    setState({ at: "sending" });
    rec.stop()
      .then((audio) =>
        audio.size === 0
          ? Promise.reject(new ApiError("broken"))
          : api.recordVoice(audio),
      )
      .then(handle)
      .catch((e) =>
        setState({
          at: "trouble",
          text:
            e instanceof ApiError && e.kind === "too-long"
              ? "Запись длинновата. Скажи короче."
              : e instanceof ApiError && e.kind === "offline"
                ? "Нет связи. Попробуй ещё раз."
                : "Не получилось разобрать запись. Попробуй ещё раз или набери текстом.",
        }),
      );
  };

  const busy = state.at === "sending";
  const recording = state.at === "recording";
  /* Голос и отправка делят одно место справа, а не стоят рядом. Пока сказать
     нечего — микрофон; появился текст — стрелка. Раньше показывались оба, и
     второй был мёртв: выключенная кнопка на пустом поле нажималась и молчала,
     а нарисованный на ней плюс обещал «добавить что-то ещё». */
  const showVoice = canRecord && !recording && !text.trim();

  return (
    <section style={{ marginTop: "var(--s-3)" }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim() && !busy) run(() => api.record(text.trim()));
        }}
        style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                 background: "var(--surface)", borderRadius: "var(--r-card)",
                 padding: "10px 12px 10px 16px" }}
      >
        {recording ? (
          <div style={{ flexGrow: 1, display: "flex", alignItems: "center", gap: "var(--s-3)" }}>
            <span aria-hidden="true"
                  style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--danger)",
                           animation: "pulse 1.2s ease-in-out infinite", flexShrink: 0 }} />
            <span className="num" role="timer" aria-label={`Идёт запись, ${seconds} секунд`}
                  style={{ fontSize: "var(--f-body)" }}>
              {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
            </span>
            <button type="button" onClick={() => stopVoice(false)}
                    style={{ marginLeft: "auto", minHeight: 38, padding: "0 12px",
                             background: "none", border: "none", cursor: "pointer",
                             font: "inherit", fontSize: "var(--f-body-s)",
                             color: "var(--text-muted)" }}>
              Отмена
            </button>
          </div>
        ) : (
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Кофе 80к, такси 5 баксов…"
            aria-label="Записать трату фразой"
            style={{ flexGrow: 1, minWidth: 0, background: "none", border: "none",
                     color: "var(--text)", font: "inherit",
                     fontSize: "var(--f-body)" }}
          />
        )}

        {showVoice && (
          <button type="button" onClick={startVoice} aria-disabled={busy || undefined} aria-label="Записать голосом"
                  style={{ width: 44, height: 44, borderRadius: "var(--r-pill)", border: "none",
                           flexShrink: 0, background: "none", cursor: "pointer",
                           display: "flex", alignItems: "center", justifyContent: "center",
                           color: "var(--text-muted)" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5.5 11.5a6.5 6.5 0 0013 0" />
              <path d="M12 18v3" />
            </svg>
          </button>
        )}
        {!showVoice && (
        <button
          type={recording ? "button" : "submit"}
          onClick={recording ? () => stopVoice(true) : undefined}
          disabled={!recording && (!text.trim() || busy)}
          aria-label={recording ? "Остановить и записать" : "Записать"}
          style={{ width: 36, height: 36, borderRadius: "var(--r-pill)", border: "none",
                   flexShrink: 0, cursor: text.trim() && !busy ? "pointer" : "default",
                   background: text.trim() && !busy ? "var(--accent)" : "var(--surface-2)",
                   color: text.trim() && !busy ? "var(--on-accent)" : "var(--text-disabled)",
                   display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
               style={busy ? { animation: "spin 900ms linear infinite" } : undefined}>
            {busy
              ? <path d="M12 3a9 9 0 109 9" />
              : recording
                ? <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" />
                : /* Стрелка вверх, а не плюс: плюс в этом месте значит
                     «приложить файл», и кнопка обещала не то, что делает. */
                  <path d="M12 19V5M6 11l6-6 6 6" />}
          </svg>
        </button>
        )}
      </form>

      {busy && (
        <Note tone="ask">
          Разбираю. Обычно пара секунд.
        </Note>
      )}
      {state.at === "trouble" && <Note tone="warn">{state.text}</Note>}
      {state.at === "answer" && <Answer out={state.out} onRun={run} />}
    </section>
  );
}

function Answer({ out, onRun }: { out: RecordOut; onRun: (c: () => Promise<RecordOut>) => void }) {
  // Транскрипт — отдельной строкой над ответом (INV-12): даже когда разбор не
  // удался, человек должен видеть, что именно услышал бот, а не гадать.
  const heard = out.transcript ? (
    <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                  marginBottom: "var(--s-2)" }}>
      Услышал: «{out.transcript}»
    </div>
  ) : null;

  return <>{heard}<AnswerBody out={out} onRun={onRun} /></>;
}

function AnswerBody({ out, onRun }: {
  out: RecordOut; onRun: (c: () => Promise<RecordOut>) => void;
}) {
  switch (out.kind) {
    case "recorded": {
      const first = out.operations[0];
      if (!first) return <Note tone="ok">Записал.</Note>;
      const tail = out.operations.length > 1 ? ` и ещё ${out.operations.length - 1}` : "";
      return (
        <Note tone="ok">
          Записал: {first.description || first.category_name},{" "}
          {formatMoney(first.amount_base, first.base_currency)}
          {tail}
        </Note>
      );
    }

    case "needs_currency":
      return (
        <Note tone="ask">
          <div>«{out.title}» — в какой валюте?</div>
          <div style={{ display: "flex", gap: "var(--s-2)", marginTop: "var(--s-3)" }}>
            {out.currencies.map((code) => (
              <button key={code} type="button" onClick={() => onRun(() => api.recordCurrency(code))}
                      style={choice}>
                {code}
              </button>
            ))}
          </div>
        </Note>
      );

    case "needs_confirm":
      return (
        <Note tone="ask">
          <div className="num">
            {out.amount && formatMoney(out.amount, out.currency)} — сумма крупная, подтверди.
            {out.amount_base && (
              <span style={{ color: "var(--text-muted)" }}>
                {" "}≈ {formatMoney(out.amount_base, out.base_currency)}
              </span>
            )}
          </div>
          <button type="button" onClick={() => onRun(() => api.recordConfirm())}
                  style={{ ...choice, marginTop: "var(--s-3)" }}>
            Да, записать
          </button>
        </Note>
      );

    case "transfer_ignored":
      return <Note tone="ok">Это перевод между своими — записывать не стал.</Note>;
    case "subscription":
      return <Note tone="ok">Подписка «{out.title}» в реестре.</Note>;
    case "budget_set":
      return (
        <Note tone="ok">
          Лимит на «{out.title}» —{" "}
          {out.amount_base && formatMoney(out.amount_base, out.base_currency)}.
        </Note>
      );
    case "fx_set":
      return <Note tone="ok">Курс {out.title} записан вручную.</Note>;
    case "fx_rejected":
      return <Note tone="warn">Ручной курс задаёт только владелец бота.</Note>;
    case "base_changed":
      return <Note tone="ok">Итоги теперь в {out.base_currency}.</Note>;
    case "failed":
      return <Note tone="warn">Разбор недоступен, но фраза сохранена — повтори позже.</Note>;
    case "unclear":
      return <Note tone="ask">{out.hint || "Не понял фразу. Скажи иначе?"}</Note>;
    default:
      return <Note tone="warn">С этим лучше в чат — здесь пока не умею.</Note>;
  }
}

function Note({ tone, children }: { tone: "ok" | "ask" | "warn"; children: React.ReactNode }) {
  const color = tone === "ok" ? "var(--income)" : tone === "warn" ? "var(--warn)" : "var(--accent)";
  return (
    <div role="status" style={{ marginTop: "var(--s-2)", background: "var(--surface)",
                                borderRadius: "var(--r-card)", padding: "12px 16px",
                                fontSize: "var(--f-body-s)", lineHeight: 1.5,
                                borderLeft: `2px solid ${color}` }}>
      {children}
    </div>
  );
}

const choice: React.CSSProperties = {
  minHeight: 38,
  padding: "8px 16px",
  borderRadius: "var(--r-sm)",
  border: "none",
  background: "var(--surface-2)",
  color: "var(--text)",
  font: "inherit",
  fontSize: "var(--f-body-s)",
  fontWeight: 600,
  cursor: "pointer",
};
